import showToast from "../hooks/useCustomToast";
import { Header } from "build-ts/metadata";
import { AnalysisPlanProto } from "build-ts/analysis_plan";
import { PlanInput, ChatRequest, AgentType } from "build-ts/agent";

/**
 * List all plans.
 *
 * @returns {Promise<Header[]>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const getPlanHeaders = async () => {
  try {
    const response = await fetch(process.env.REACT_APP_LIST_PLANS_PATH!, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    
    if (!response.ok) {
      showToast("Error in listing plans", "", "error");
      return null;
    }
    const data = await response.json();
    return data.map((header: any) => Header.fromJSON(header));
  } catch (error) {
    showToast("Error in listing plans", "", "error");
  }
};

/**
 * Get plan.
 *
 * @param {string} planId - The id of the plan to get.
 * @returns {Promise<AnalysisPlanProto>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const getPlan = async (planId: string) => {
  const response = await fetch(`${process.env.REACT_APP_ANALYSIS_PLAN!}/${planId}`!, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    showToast("Error in getting plan", "", "error");
    return null;
  }
  const data = await response.json();
  return AnalysisPlanProto.fromJSON(data);
};

/**
 * Create/Update plan.
 *
 * @param {AnalysisPlanProto} plan - The plan to create or update.
 * @returns {Promise<AnalysisPlanProto>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const createOrUpdatePlan = async (plan: AnalysisPlanProto) => {
  const response = await fetch(process.env.REACT_APP_CREATE_OR_UPDATE_PLAN_PATH!, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(AnalysisPlanProto.toJSON(plan)),
  });

  if (!response.ok) {
    showToast("Error in creating or updating plan", "", "error");
    return null;
  }
  const data = await response.json();
  return AnalysisPlanProto.fromJSON(data);
};

/**
 * Update plan header
 * 
 * @param {string} planId - The id of the plan to update.
 * @param {string} name - The name of the plan.
 * @returns {Promise<void>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const updatePlanHeader = async (planId: string, name: string, description?: string) => {
  const response = await fetch(
    `${process.env.REACT_APP_UPDATE_PLAN_HEADER!}?id=${planId}&name=${name}&description=${description}`!, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    showToast("Error in updating plan header", "", "error");
    return null;
  }
  return response;
};

/**
 * Delete plan
 * 
 * @param {string} planId - The id of the plan to delete.
 * @returns {Promise<void>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const deletePlan = async (planId: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ANALYSIS_PLAN!}/${planId}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );

    if (response.ok) {
      showToast("Plan deleted successfully", "", "success");
      return response;
    }
    if(response.status === 409) {
      const responseJson = await response.json();
      return responseJson;
    }
    showToast("Failed to delete plan", "", "error");
    return response;
  } catch (error) {
    showToast("Failed to delete plan", "", "error");
  }
};

/**
 * Search plans headers.
 *
 * @param {number} pageSize - The page size.
 * @param {number} pageNumber - The page number.
 * @param {string} sortByField - The field to sort by.
 * @param {boolean} sortAscending - The sort ascending.
 * @param {string} searchString - The search string.
 * @param {string[]} metricIds - The metric ids.
 * @param {boolean} includeScheduleActivity - whether header response includes the field hasActiveSchedule
 * @param {boolean} activeSchedule - sort on the basis of active/inactive schedule
 * @returns {Promise<Header[]>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const searchPlansheaders = async (
  pageSize: Number, 
  pageNumber: Number, 
  sortByField: string, 
  sortAscending: boolean, 
  searchString: string, 
  metricIds: string[],
  includeScheduleActivity: boolean = false,
  activeSchedule: boolean | ""
) => {

  const metricIdsParam = encodeURIComponent(JSON.stringify(metricIds));
  try {
    const response = await fetch(
      `${process.env.REACT_APP_LIST_PLANS_PATH}?pageSize=${pageSize}&pageNumber=${pageNumber}&sortByField=${sortByField}&sortAscending=${sortAscending}&searchString=${searchString}&
      metricIds=${metricIdsParam}&includeScheduleActivity=${includeScheduleActivity}&activeSchedule=${activeSchedule}`,
      {
        method: "GET",
        credentials: "include",
      }
    );
    
    if (!response.ok) {
      showToast("Error in listing plans", "", "error");
      return null;
    }
    const data = await response.json();
    return {headers: data.headers.map((header: Header[]) => Header.fromJSON(header)), totalRows: data.total_rows};
  } catch (error) {
    showToast("Error in searching plans", "", "error");
  }
};

/**
 * Clone a plan.
 *
 * @param {string} planId - The id of the plan to clone.
 * @param {boolean} save - Whether to save the cloned plan to database (default: false).
 * @returns {Promise<AnalysisPlanProto | null>} A promise that resolves to the cloned plan.
 * @throws {Error} Throws an error if the request fails.
 */
export const clonePlan = async (planId: string, save: boolean = false) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_CLONE_PLAN}?planId=${planId}&save=${save}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      showToast("Error in cloning plan", "", "error");
      return null;
    }
    
    const data = await response.json();
    showToast("Plan cloned successfully", "", "success");
    return AnalysisPlanProto.fromJSON(data);
  } catch (error) {
    showToast("Error in cloning plan", "", "error");
    return null;
  }
};

/**
 * Enrich a plan.
 * 
 * @param {PlanInput} planInput - The plan input.
 * @param {string} instruction - The instruction.
 * @returns {Promise<AnalysisPlanProto | null>} A promise that resolves to the enriched plan.
 * @throws {Error} Throws an error if the request fails.
 */
export const enrichPlan = async (planInput: PlanInput, instruction: string | "") => {
  try{
    const response = await fetch(process.env.REACT_APP_RUN_TIGA!, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ChatRequest.toJSON(
        ChatRequest.create({
          agent_type: AgentType.PLAN,
          stream: false,
          user_messages: instruction !== "" ? [instruction] : [],
          plan_input: planInput,
          client_tag: "FRIDA"
        })
      )),
    });

    if(!response.ok){
      showToast("Error in enriching plan", "", "error");
      return null;
    }
    const data = await response.json();
    return data;
  } catch(error){
    showToast("Error in enriching plan", "", "error");
    return null;
  }
};

/**
 *  Generate a node label.
 * 
 * @param {PlanInput} planInput - The plan input.
 * @returns {Promise<string | null>} A promise that resolves to the node label.
 * @throws {Error} Throws an error if the request fails.
 */
export const generateNodeLabel = async(planInput: PlanInput) => {
  try{
    const response = await fetch(process.env.REACT_APP_RUN_TIGA!, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ChatRequest.toJSON(
        ChatRequest.create({
          agent_type: AgentType.PLAN_STEP_LABEL,
          stream: false,
          user_messages: [],
          plan_input: planInput,
          client_tag: "FRIDA"
        })
      )),
    });

    if(!response.ok){
      showToast("Error in generating node label", "", "error");
      return null;
    }
    const data = await response.json();
    showToast("Step label generated successfully", "", "success");
    return data;
  } catch(error){
    showToast("Error in generating node label", "", "error");
    return null;
  }
};